import React from "react";
import { CircularProgress, Modal } from "@mui/material";
import "./EmployeeStatusConfirmModal.scss";

export type ConfirmType = "activate" | "deactivate" | "archive" | "unarchive";

export const getConfirmConfig = (type: ConfirmType) => {
  switch (type) {
    case "activate":
      return {
        title: "Activate employee?",
        lines: [
          "Are you sure you want to reactivate this employee?",
          "Reactivating this account will enable all associated permissions and allow the employee to access their dashboard.",
        ],
        note: "",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "active",
      };

    case "deactivate":
      return {
        title: "Deactivate employee?",
        lines: [
          "Are you sure you want to deactivate this employee?",
          "This employee will no longer be able to log in or access any system resources.",
        ],
        note: "",
        confirmText: "Deactivate",
        confirmVariant: "danger" as const,
        nextStatus: "deactivated",
      };

    case "archive":
      return {
        title: "Archive employee?",
        lines: [
          "Archiving hides this employee from your main view to keep your workspace organized.",
          "You can still access their history and reports later.",
        ],
        note: "The employee will lose all login access and system permissions immediately.",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "archived",
      };

    case "unarchive":
      return {
        title: "Unarchive employee?",
        lines: [
          "This will move the employee back to your main workspace and active list.",
        ],
        note: "Their login access and system permissions will be restored, allowing them to access the platform again.",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "active",
      };
  }
};

interface Props {
  open: boolean;
  type: ConfirmType | null;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const EmployeeStatusConfirmModal = ({
  open,
  type,
  loading = false,
  onCancel,
  onConfirm,
}: Props) => {
  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onCancel}
      className="employee-confirm-modal"
      disableEscapeKeyDown={loading}
    >
      <div className="ecm-center" onClick={(e) => e.stopPropagation()}>
        <div className="ecm-card">
          {type && (
            <>
              {(() => {
                const cfg = getConfirmConfig(type);

                return (
                  <>
                    <p className="ecm-title">{cfg.title}</p>

                    <div className="ecm-body">
                      {cfg.lines.map((t, idx) => (
                        <p key={idx} className="ecm-text">
                          {t}
                        </p>
                      ))}

                      {cfg.note ? (
                        <div className="ecm-note">
                          <p className="ecm-note-title">Note:</p>
                          <p className="ecm-note-text">{cfg.note}</p>
                        </div>
                      ) : null}
                    </div>

                    <div className="ecm-actions">
                      <button
                        className="ecm-cancel"
                        onClick={onCancel}
                        disabled={loading}
                        type="button"
                      >
                        Cancel
                      </button>

                      <button
                        className={`ecm-confirm ${
                          cfg.confirmVariant === "danger" ? "danger" : ""
                        }`}
                        onClick={onConfirm}
                        disabled={loading}
                        type="button"
                      >
                        {loading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          cfg.confirmText
                        )}
                      </button>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EmployeeStatusConfirmModal;